import { apiRequest } from '../../shared/api/client';

/**
 * Pricing — mirrors Invetrak.Api.Endpoints.PricingEndpoints.
 *
 *   GET /api/v1/items/{id}/price-tiers        pricing:read
 *   PUT /api/v1/items/{id}/price-tiers        pricing:write
 *   GET /api/v1/customers/{id}/pricing        pricing:read
 *   PUT /api/v1/customers/{id}/pricing        pricing:write
 *   GET /api/v1/pricing/resolve               orders:read
 *
 * **pricing:read, not items:read.** A tier structure is margin information, and every seeded
 * WarehouseOperator holds items:read. The resolved price on a line they are picking stays under
 * orders:read; what pricing:read gates is the structure behind it.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/** legacy's codes. 1 is the column default, so it prices every customer nobody has configured. */
export const PRICE_METHOD_STANDARD = 1;
export const PRICE_METHOD_PERCENTAGE = 2;
export const PRICE_METHOD_CONTRACT = 3;

/**
 * One volume tier on an item.
 *
 * `minQuantity` is in the item's STOCKING unit whatever unit an order line is entered in — a tier
 * of 10 on a case-stocked item means ten cases, and one case of twelve reaches a ten tier on an
 * each-stocked one. The grid labels the column with the stocking UOM code so that is visible where
 * it is typed rather than discovered on an invoice.
 *
 * `discount` is a FRACTION: 0.10 is ten per cent. The screen shows a percentage.
 */
export interface ItemPriceTierDto {
  itemPriceTierId: string;
  itemId: string;
  minQuantity: number;
  discount: number;
}

export interface CustomerPricingDto {
  customerId: string;
  customerNumber: string;
  customerName: string;
  /** 1 standard (item tiers), 2 percentage, 3 contract. */
  priceMethod: number;
  /** A fraction, and null unless the method is 2. */
  percentageDiscount: number | null;
}

export interface ResolvedPriceDto {
  itemId: string;
  uomId: string;
  quantity: number;
  /** What the tiers were compared against, in the item's stocking unit. */
  stockingQuantity: number;
  /** List price, per the ENTERED unit. */
  listUnitPrice: number;
  discount: number;
  /** Net price, per the ENTERED unit. This is what goes on the line. */
  unitPrice: number;
  priceMethod: number;
  /** list, item_tier, customer_percentage, contract or override. */
  priceSource: string;
  itemPriceTierId: string | null;
}

export async function listItemPriceTiers(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemPriceTierDto[]> {
  const { data } = await apiRequest<ItemPriceTierDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/price-tiers`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Replaces the whole structure.
 *
 * The grid IS the structure, so what it submits is what the item has and an empty list removes the
 * tiers altogether. There is no per-tier delete for that reason.
 */
export async function setItemPriceTiers(
  itemId: string,
  tiers: { minQuantity: number; discount: number }[],
): Promise<ItemPriceTierDto[]> {
  const { data } = await apiRequest<ItemPriceTierDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/price-tiers`,
    { method: 'PUT', body: { tiers }, ...COOKIE_INIT },
  );
  return data;
}

export async function getCustomerPricing(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerPricingDto> {
  const { data } = await apiRequest<CustomerPricingDto>(
    `/v1/customers/${encodeURIComponent(customerId)}/pricing`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function setCustomerPricing(
  customerId: string,
  body: { priceMethod: number; percentageDiscount?: number | null },
): Promise<CustomerPricingDto> {
  const { data } = await apiRequest<CustomerPricingDto>(
    `/v1/customers/${encodeURIComponent(customerId)}/pricing`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

/** What a line would cost, without saving one — so the order screen can price as you type. */
export async function resolvePrice(
  query: { itemId: string; quantity: number; uomId?: string | null; customerId?: string | null },
  signal?: AbortSignal,
): Promise<ResolvedPriceDto> {
  const params = new URLSearchParams({
    itemId: query.itemId,
    quantity: String(query.quantity),
  });
  if (query.uomId) params.set('uomId', query.uomId);
  if (query.customerId) params.set('customerId', query.customerId);

  const { data } = await apiRequest<ResolvedPriceDto>(`/v1/pricing/resolve?${params}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * A fraction to the percentage a person types, and back.
 *
 * In one place, because getting it wrong in either direction is a hundredfold pricing error and
 * the two halves must agree. Rounded to four decimal places of a percent, which is the precision
 * the column holds.
 */
export function fractionToPercent(fraction: number): number {
  return Math.round(fraction * 1000000) / 10000;
}

export function percentToFraction(percent: number): number {
  return Math.round(percent * 10000) / 1000000;
}

// --- contract pricing, M5.15 --------------------------------------------------
//
//   GET    /api/v1/customers/{id}/price-contracts             pricing:read
//   POST   /api/v1/customers/{id}/price-contracts             pricing:write
//   PUT    /api/v1/customers/{id}/price-contracts/in-force    pricing:write
//   GET    /api/v1/price-contracts/{id}                       pricing:read
//   PUT    /api/v1/price-contracts/{id}                       pricing:write
//   GET    /api/v1/price-contracts/{id}/lines                 pricing:read
//   PUT    /api/v1/price-contracts/{id}/items/{itemId}        pricing:write
//
// All behind the Pricing add-on, like the rest of this file.

/**
 * A customer's negotiated prices.
 *
 * `inForce` is three questions answered together — active, unexpired, and the one the customer
 * NAMES — because a contract can satisfy any two of them and price nothing.
 */
export interface PriceContractDto {
  priceContractId: string;
  customerId: string;
  customerNumber: string;
  customerName: string;
  contractNumber: string;
  description: string | null;
  /** Null means it does not expire. */
  expiresAtUtc: string | null;
  isActive: boolean;
  createdAtUtc: string;
  lastModifiedUtc: string;
  itemCount: number;
  inForce: boolean;
}

/**
 * One negotiated price, from a quantity up.
 *
 * `unitPrice` is ABSOLUTE money per STOCKING unit — the whole difference from an item tier, which
 * is a fraction off list. `minQuantity` is in stocking units, as the item tiers are.
 */
export interface PriceContractLineDto {
  priceContractLineId: string;
  priceContractId: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  stockingUomCode: string;
  minQuantity: number;
  unitPrice: number;
  listUnitPrice: number;
}

export async function listPriceContracts(
  customerId: string,
  signal?: AbortSignal,
): Promise<PriceContractDto[]> {
  const { data } = await apiRequest<PriceContractDto[]>(
    `/v1/customers/${encodeURIComponent(customerId)}/price-contracts`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getPriceContract(
  priceContractId: string,
  signal?: AbortSignal,
): Promise<PriceContractDto> {
  const { data } = await apiRequest<PriceContractDto>(
    `/v1/price-contracts/${encodeURIComponent(priceContractId)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createPriceContract(
  customerId: string,
  body: { contractNumber: string; description?: string | null; expiresAtUtc?: string | null },
): Promise<PriceContractDto> {
  const { data } = await apiRequest<PriceContractDto>(
    `/v1/customers/${encodeURIComponent(customerId)}/price-contracts`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
      ...COOKIE_INIT,
    },
  );
  return data;
}

/** Which contract prices this customer's orders. Null takes them off contract pricing. */
export async function setPriceContractInForce(
  customerId: string,
  priceContractId: string | null,
): Promise<void> {
  await apiRequest<void>(
    `/v1/customers/${encodeURIComponent(customerId)}/price-contracts/in-force`,
    {
      method: 'PUT',
      body: JSON.stringify({ priceContractId }),
      headers: { 'Content-Type': 'application/json' },
      ...COOKIE_INIT,
    },
  );
}

export async function listPriceContractLines(
  priceContractId: string,
  itemId?: string,
  signal?: AbortSignal,
): Promise<PriceContractLineDto[]> {
  const query = itemId ? `?itemId=${encodeURIComponent(itemId)}` : '';
  const { data } = await apiRequest<PriceContractLineDto[]>(
    `/v1/price-contracts/${encodeURIComponent(priceContractId)}/lines${query}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Replaces one item's prices. An empty list takes the item off the contract. */
export async function setPriceContractLines(
  priceContractId: string,
  itemId: string,
  tiers: { minQuantity: number; unitPrice: number }[],
): Promise<PriceContractLineDto[]> {
  const { data } = await apiRequest<PriceContractLineDto[]>(
    `/v1/price-contracts/${encodeURIComponent(priceContractId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ tiers }),
      headers: { 'Content-Type': 'application/json' },
      ...COOKIE_INIT,
    },
  );
  return data;
}
